import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useProperty, useSiteContext, useTranslation } from '@eti/providers';
import { ContentWrapper } from '@eti/components';
import { css, etiColors } from '@eti/styles';
import { Collection, DevBar } from '@eti/debug';
import { useQuery } from '@apollo/client';
import { breakpoints, Column, Columns, Stack } from '@etg/wings';
import Header from '../../../widgets/customer-support/Header';
import { DebugButton, DebugCommonComponents } from '../../../common/debug/components';
import { isDev } from '../../../common/debug/utils';
import { LANDING, PRE_BOOKING } from '../utils/constants';
import { isWhiteLabel } from '../../my-bookings/utils/utils';
import { CONTACT_US_SUPPORT_FAQ } from '../containers/queries';
import FaqSection from '../../order-details/components/common/FaqSection';
import Selection from './Selection';
import Refund from './Refund';
import PreBooking from './PreBooking';

const sectionStyles = css`
  background-color: ${etiColors.grayScale[5]};
  color: ${etiColors.black};
  height: 100%;
`;

const contentWrapperStyles = css`
  margin-bottom: 40px;
`;

const ContactUs = ({ clearForm, fillForm }) => {
  const { p } = useProperty();
  const { t } = useTranslation();
  const { agencyAccount, brand } = useSiteContext();
  const location = useLocation();
  const [pageStatus, setPageStatus] = useState(LANDING);
  const headerRef = useRef(null);
  const isWhiteLabelBrand = isWhiteLabel(brand.code, p('MyBookingsWelcomePage.WhiteLabelBrands'));

  const { data: faqSectionData } = useQuery(CONTACT_US_SUPPORT_FAQ, {
    fetchPolicy: 'cache-first',
    variables: { sectionSlug: p('ContactUs.Support.CommonQuestions.Faq.Slug') },
    skip: !p('ContactUs.FAQ.Enabled') || isWhiteLabelBrand || agencyAccount,
  });

  const contactUsFaqSection = faqSectionData?.faq?.sections[0];

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const status = query.get('status');

    if (status === PRE_BOOKING) {
      setPageStatus(status);
    }
  }, [location.search]);

  const headerTexts = {
    [LANDING]: {
      heading: t('ContactUs.Landing.Header'),
      subheading: t('ContactUs.Landing.Description'),
    },
    [PRE_BOOKING]: {
      heading: t('ContactUs.PreBooking.Header'),
      subheading: t('ContactUs.PreBooking.Description'),
    },
  };

  const handleNotHavingOrder = () => {
    setPageStatus(PRE_BOOKING);
    headerRef.current.focus();
  };

  return (
    <>
      <section className={sectionStyles} data-testid="contact-us">
        <Header
          data-testid="contact-us-header"
          ref={headerRef}
          subtitle={headerTexts[pageStatus].subheading}
          title={headerTexts[pageStatus].heading}
        />
        <ContentWrapper className={contentWrapperStyles}>
          {pageStatus === LANDING && (
            <Stack>
              <Selection clearForm={clearForm} handleNoOrder={handleNotHavingOrder} />
              <Columns spacing={16}>
                <Column width={{ [breakpoints._0]: '12/12', [breakpoints._768]: '6/12' }}>
                  <Refund />
                </Column>
                <Column width={{ [breakpoints._0]: '12/12', [breakpoints._768]: '6/12' }}>
                  {contactUsFaqSection && <FaqSection section={contactUsFaqSection} />}
                </Column>
              </Columns>
            </Stack>
          )}
          {pageStatus === PRE_BOOKING && (
            <PreBooking faqSection={contactUsFaqSection} setPageStatus={setPageStatus} />
          )}
        </ContentWrapper>
      </section>
      {isDev && (
        <DevBar>
          <Collection>
            <DebugButton onClick={clearForm}>Reset Form</DebugButton>
            <DebugButton onClick={fillForm}>Fill Form</DebugButton>
            <DebugCommonComponents />
          </Collection>
        </DevBar>
      )}
    </>
  );
};

ContactUs.propTypes = {
  clearForm: PropTypes.func,
  fillForm: PropTypes.func,
};

export default ContactUs;
